import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { uploadSiteImage } from '@/lib/siteImageApi';
import { SITE_IMAGE_KEYS } from '@/constants/siteImageKeys';
import { useToast } from '@/components/ui/ToastContext';
import i18n from '@/i18n';

export type Locale = 'en' | 'ko' | 'bn';

interface ContentRow {
  key: string;
  locale: string;
  value: string;
}

// Shared data/save logic behind every per-page "premium" section editor
// (Home, Brand Story, and everything after it) — one shared
// rxk_page_sections table (keyed by pageKey), the existing
// rxk_site_content/rxk_site_images mechanisms for text/images. Each page's
// admin file only needs its own section config array; this hook (and
// SectionPageEditor, which renders it) is identical across all of them.
export function useSectionPageEditor(pageKey: string) {
  const { push } = useToast();
  const [loading, setLoading] = useState(true);
  const [sectionVisibility, setSectionVisibility] = useState<Record<string, boolean>>({});
  const [contentRows, setContentRows] = useState<ContentRow[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [locale, setLocale] = useState<Locale>('en');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [fieldStatus, setFieldStatus] = useState<Record<string, 'saving' | 'saved'>>({});
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [sectionsRes, contentRes, imagesRes] = await Promise.all([
      supabase.from('rxk_page_sections').select('section_key, is_visible').eq('page_key', pageKey),
      supabase.from('rxk_site_content').select('key, locale, value'),
      supabase.from('rxk_site_images').select('key, url'),
    ]);
    const visibility: Record<string, boolean> = {};
    for (const row of (sectionsRes.data as { section_key: string; is_visible: boolean }[]) ?? []) visibility[row.section_key] = row.is_visible;
    setSectionVisibility(visibility);
    setContentRows((contentRes.data as ContentRow[]) ?? []);
    const images: Record<string, string> = {};
    for (const row of (imagesRes.data as { key: string; url: string }[]) ?? []) images[row.key] = row.url;
    setImageUrls(images);
    setLoading(false);
  }, [pageKey]);

  useEffect(() => {
    load();
  }, [load]);

  // No DB row for this key/locale doesn't mean the site shows nothing —
  // it means nobody has overridden it yet, so the live page falls back to
  // the bundled locale-file default (see src/i18n/index.ts). Falling back
  // to '' here instead showed every never-touched field as blank in admin
  // even though real text was live on the site — this reads the exact same
  // resource bundle the site itself renders from, so what's in the field
  // always matches what's actually showing, override or not.
  const savedValue = (key: string, loc: Locale) =>
    contentRows.find((r) => r.key === key && r.locale === loc)?.value ?? (i18n.getResource(loc, 'translation', key) as string | undefined) ?? '';
  const draftKey = (key: string, loc: Locale) => `${key}:${loc}`;
  const fieldValue = (key: string) => {
    const dk = draftKey(key, locale);
    return dk in draft ? draft[dk] : savedValue(key, locale);
  };
  const imageFor = (key: string) => imageUrls[key] ?? SITE_IMAGE_KEYS.find((d) => d.key === key)?.fallbackUrl ?? '';
  const sizeHintFor = (key: string) => SITE_IMAGE_KEYS.find((d) => d.key === key)?.recommendedSize ?? '';
  const isVisible = (sectionKey: string) => sectionVisibility[sectionKey] !== false;

  const toggleSectionVisibility = async (sectionKey: string, next: boolean) => {
    setSectionVisibility((prev) => ({ ...prev, [sectionKey]: next }));
    const { data: { user } } = await supabase.auth.getUser();
    // Newer pages (e.g. the new-design Home) have no rows yet: insert on first toggle.
    const { data: updated, error: updateError } = await supabase
      .from('rxk_page_sections')
      .update({ is_visible: next, updated_by: user?.id ?? null })
      .eq('page_key', pageKey)
      .eq('section_key', sectionKey)
      .select('section_key');
    const { error } =
      updateError || (updated && updated.length > 0)
        ? { error: updateError }
        : await supabase
            .from('rxk_page_sections')
            .insert({ page_key: pageKey, section_key: sectionKey, label: sectionKey, is_visible: next, updated_by: user?.id ?? null });
    if (error) {
      push('error', error.message);
      setSectionVisibility((prev) => ({ ...prev, [sectionKey]: !next }));
    } else {
      push('success', next ? 'Section shown on the live page' : 'Section hidden from the live page');
    }
  };

  const uploadImage = async (key: string, file: File) => {
    setUploadingKey(key);
    try {
      await uploadSiteImage(key, file);
      push('success', 'Image updated — live on the site immediately');
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingKey(null);
    }
  };

  const handleFieldChange = (key: string, value: string) => {
    setDraft((prev) => ({ ...prev, [draftKey(key, locale)]: value }));
    setFieldStatus((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleFieldBlur = async (key: string) => {
    const dk = draftKey(key, locale);
    const value = draft[dk];
    if (value === undefined || value === savedValue(key, locale)) return;
    setFieldStatus((prev) => ({ ...prev, [key]: 'saving' }));
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from('rxk_site_content').upsert({ key, locale, value, updated_by: user?.id ?? null }, { onConflict: 'key,locale' });
    if (error) {
      push('error', error.message);
      setFieldStatus((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setContentRows((prev) => {
      const idx = prev.findIndex((r) => r.key === key && r.locale === locale);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { key, locale, value };
        return next;
      }
      return [...prev, { key, locale, value }];
    });
    setFieldStatus((prev) => ({ ...prev, [key]: 'saved' }));
    setTimeout(() => {
      setFieldStatus((prev) => {
        if (prev[key] !== 'saved') return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, 1500);
  };

  return {
    loading,
    locale,
    setLocale,
    isVisible,
    toggleSectionVisibility,
    fieldValue,
    handleFieldChange,
    handleFieldBlur,
    fieldStatus,
    imageFor,
    sizeHintFor,
    uploadingKey,
    uploadImage,
  };
}
